import { DateTime } from 'luxon';
import type {
    AppAggregateValue,
    AppColumn,
    AppColumnGroup,
    AppExportContext,
    AppExportValue,
    AppGroupNode,
    AppScalarValue,
} from './AppTable.types';

export interface AppExportColumn {
    id: string;
    label: string;
    scalarType?: AppColumn<unknown>['scalarType'];
    source?: AppColumn<unknown>;
}

export type AppProjectedRow =
    | { kind: 'group' | 'grandTotal'; values: readonly AppExportValue[]; displayValues: readonly string[] }
    | { kind: 'leaf'; rowId: string | number; values: readonly AppExportValue[]; displayValues: readonly string[] };

export interface AppExportProjection {
    columns: readonly AppExportColumn[];
    /** Group header labels at each level, followed by the leaf header labels. */
    headerRows: readonly (readonly string[])[];
    headerMerges: readonly { rowIndex: number; startColumn: number; endColumn: number }[];
    rows: readonly AppProjectedRow[];
    metadataRows: readonly string[];
    fileName?: string;
    title: string;
    timestamp?: string;
}

const GROUP_COLUMN_ID = '__app_export_group_label__';
const GROUP_COLUMN_LABEL = 'Group';

function getAggregates(aggregates: readonly AppAggregateValue[]): Map<string, number> {
    return new Map(aggregates.map(({ columnId, value }) => [columnId, value]));
}

function groupLeafCount<TRow>(node: AppGroupNode<TRow>): number {
    return (
        node.leafCount ??
        node.children.reduce((count, child) => count + (child.kind === 'group' ? groupLeafCount(child) : 1), 0)
    );
}

function flattenRows<TRow>(
    rows: AppExportContext<TRow>['rows'],
    projectionColumns: readonly AppExportColumn[],
    hasSyntheticLabel: boolean,
): AppProjectedRow[] {
    const projected: AppProjectedRow[] = [];
    const sourceColumns = projectionColumns.filter((column) => column.source);
    const isGrouped = rows.some((row) => row.kind === 'group');

    const aggregateValues = (aggregates: readonly AppAggregateValue[]): AppExportValue[] => {
        const byId = getAggregates(aggregates);

        return projectionColumns.map((column) => (column.source ? (byId.get(column.id) ?? null) : null));
    };

    const visit = (items: AppExportContext<TRow>['rows']) => {
        for (const item of items) {
            if (item.kind === 'group') {
                const values = aggregateValues(item.aggregates);
                const count = groupLeafCount(item);
                const label = `${'  '.repeat(item.depth)}${item.label} (${count} ${count === 1 ? 'row' : 'rows'})`;

                if (hasSyntheticLabel) {
                    values[0] = label;
                }

                const byId = getAggregates(item.aggregates);
                const displayValues = projectionColumns.map((column) =>
                    column.source
                        ? byId.has(column.id)
                            ? (column.source.aggregateFormatter?.(byId.get(column.id)!) ?? String(byId.get(column.id)!))
                            : ''
                        : '',
                );

                if (hasSyntheticLabel) {
                    displayValues[0] = label;
                }
                projected.push({
                    kind: 'group',
                    values,
                    displayValues,
                });
                visit(item.children);
            } else if (item.kind === 'leaf') {
                const values: AppExportValue[] = [];
                const displayValues: string[] = [];

                for (const column of sourceColumns) {
                    const source = column.source as AppColumn<TRow>;
                    const value = source.accessor(item.row);
                    const formatted = source.formatter?.(value, item.row) ?? defaultFormatted(value, source, item.row);
                    const cellContext = {
                        row: item.row,
                        value,
                        formatted,
                    };

                    values.push(
                        source.exportAccessor
                            ? source.exportAccessor(cellContext)
                            : defaultExportValue(value, source, item.row),
                    );
                    displayValues.push(formatted);
                }

                if (hasSyntheticLabel) {
                    values.unshift(null);
                    displayValues.unshift('');
                }
                projected.push({
                    kind: 'leaf',
                    rowId: item.rowId,
                    values,
                    displayValues,
                });
            } else {
                const totals = aggregateValues(item.aggregates);

                if (hasSyntheticLabel) {
                    totals[0] = 'Grand Total';
                } else if (!isGrouped) {
                    const labelIndex = projectionColumns.findIndex((column) => column.source?.aggregate !== 'sum');

                    if (labelIndex >= 0) {
                        totals[labelIndex] = 'Grand Total';
                    }
                }

                const byId = getAggregates(item.aggregates);
                const displayValues = projectionColumns.map((column) =>
                    column.source
                        ? byId.has(column.id)
                            ? (column.source.aggregateFormatter?.(byId.get(column.id)!) ?? String(byId.get(column.id)!))
                            : ''
                        : '',
                );
                const labelIndex = hasSyntheticLabel
                    ? 0
                    : projectionColumns.findIndex((column) => column.source?.aggregate !== 'sum');

                if (labelIndex >= 0) {
                    displayValues[labelIndex] = 'Grand Total';
                }
                projected.push({
                    kind: 'grandTotal',
                    values: totals,
                    displayValues,
                });
            }
        }
    };

    visit(rows);

    return projected;
}

function defaultFormatted<TRow>(value: AppScalarValue, column: AppColumn<TRow>, row: TRow): string {
    if (value === null || value === undefined) {
        return '';
    }

    if (column.scalarType === 'lookup') {
        return (
            column.lookup?.labelFor?.(value, row) ??
            column.lookup?.options?.find((option) => Object.is(option.value, value))?.label ??
            String(value)
        );
    }

    if (value instanceof Date) {
        const date = DateTime.fromJSDate(value);

        return date.isValid ? date.toFormat(column.scalarType === 'date' ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm') : '';
    }

    if ((column.scalarType === 'date' || column.scalarType === 'dateTime') && typeof value === 'string') {
        const date = DateTime.fromISO(value, {
            setZone: true,
        });

        return date.isValid ? date.toFormat(column.scalarType === 'date' ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm') : value;
    }

    return String(value);
}

function defaultExportValue<TRow>(value: AppScalarValue, column: AppColumn<TRow>, row: TRow): AppExportValue {
    if (value === null || value === undefined || value === '') {
        return value;
    }

    if (column.scalarType === 'lookup') {
        return (
            column.lookup?.labelFor?.(value, row) ??
            column.lookup?.options?.find((option) => Object.is(option.value, value))?.label ??
            value
        );
    }

    if (column.scalarType !== 'date' && column.scalarType !== 'dateTime') {
        return value;
    }

    if (value instanceof Date) {
        if (column.scalarType === 'date') {
            return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
        }

        return value;
    }

    if (typeof value !== 'string') {
        return value;
    }

    const parsed = DateTime.fromISO(value, {
        setZone: true,
    });

    if (!parsed.isValid) {
        return value;
    }

    if (column.scalarType === 'date') {
        const parts = parsed.toObject();

        if (parts.year === undefined || parts.month === undefined || parts.day === undefined) {
            return value;
        }

        return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    }

    return parsed.toJSDate();
}

function columnHeaderRows(columns: readonly AppExportColumn[]): string[][] {
    const sourceColumns = columns.filter((column) => column.source);
    const maxDepth = sourceColumns.reduce((depth, column) => {
        let current: AppColumnGroup | undefined = column.source?.groupHeader;
        let columnDepth = 0;

        while (current) {
            columnDepth++;
            current = current.parentId
                ? sourceColumns.find((candidate) => candidate.source?.groupHeader?.id === current?.parentId)?.source
                      ?.groupHeader
                : undefined;
        }

        return Math.max(depth, columnDepth);
    }, 0);
    const headers = Array.from(
        {
            length: maxDepth + 1,
        },
        () => Array<string>(columns.length).fill(''),
    );

    columns.forEach((column, index) => {
        if (!column.source) {
            headers[maxDepth][index] = column.label;

            return;
        }

        const path: string[] = [];
        let group: AppColumnGroup | undefined = column.source.groupHeader;

        while (group) {
            path.unshift(group.label);
            group = group.parentId
                ? sourceColumns.find((candidate) => candidate.source?.groupHeader?.id === group?.parentId)?.source
                      ?.groupHeader
                : undefined;
        }
        path.forEach((label, depth) => {
            headers[depth][index] = label;
        });
        headers[maxDepth][index] = column.source.headerLabel ?? column.source.label;
    });

    return headers;
}

function groupPath(column: AppExportColumn, columns: readonly AppExportColumn[]): AppColumnGroup[] {
    const path: AppColumnGroup[] = [];
    let group: AppColumnGroup | undefined = column.source?.groupHeader;

    while (group) {
        path.unshift(group);
        group = group.parentId
            ? columns.find((candidate) => candidate.source?.groupHeader?.id === group?.parentId)?.source?.groupHeader
            : undefined;
    }

    return path;
}

function columnHeaderMerges(
    columns: readonly AppExportColumn[],
    headerRowCount: number,
): Array<{ rowIndex: number; startColumn: number; endColumn: number }> {
    const merges: Array<{ rowIndex: number; startColumn: number; endColumn: number }> = [];

    for (let rowIndex = 0; rowIndex < headerRowCount - 1; rowIndex++) {
        let start = -1;
        let key: string | undefined;

        for (let columnIndex = 0; columnIndex <= columns.length; columnIndex++) {
            const nextKey =
                columnIndex < columns.length ? groupPath(columns[columnIndex], columns)[rowIndex]?.id : undefined;

            if (nextKey !== key || nextKey === undefined) {
                if (key !== undefined && start >= 0 && columnIndex - start > 1) {
                    merges.push({
                        rowIndex,
                        startColumn: start,
                        endColumn: columnIndex,
                    });
                }
                start = nextKey === undefined ? -1 : columnIndex;
                key = nextKey;
            }
        }
    }

    return merges;
}

/** Build the one ordered, full-data projection used by CSV, XLSX, and print. */
export function projectAppExport<TRow>(context: AppExportContext<TRow>, now: Date = new Date()): AppExportProjection {
    const visibleColumns = context.columns.filter((column) => column.kind !== 'action' && column.exportable !== false);
    const grouped = context.rows.some((row) => row.kind === 'group');
    const grandTotal = context.rows.some((row) => row.kind === 'grandTotal');
    const hasLabelColumn = visibleColumns.some((column) => column.aggregate !== 'sum');
    const needsSyntheticLabel = grouped || (grandTotal && !hasLabelColumn);
    const columns: AppExportColumn[] = [
        ...(needsSyntheticLabel
            ? [
                  {
                      id: GROUP_COLUMN_ID,
                      label: GROUP_COLUMN_LABEL,
                  },
              ]
            : []),
        ...visibleColumns.map((source) => ({
            id: source.id,
            label: source.headerLabel ?? source.label,
            scalarType: source.scalarType,
            source: source as AppColumn<unknown>,
        })),
    ];
    const timestamp = context.metadata.includeDateTime
        ? DateTime.fromJSDate(now).toFormat('dd/MM/yyyy hh:mm:ss')
        : undefined;
    const metadataRows = [
        ...(timestamp ? [timestamp] : []),
        ...(context.metadata.exportTitle ? [context.metadata.exportTitle] : []),
    ];
    const headerRows = columnHeaderRows(columns);

    return {
        columns,
        headerRows,
        headerMerges: columnHeaderMerges(columns, headerRows.length),
        rows: flattenRows(context.rows, columns, needsSyntheticLabel),
        metadataRows,
        fileName: context.metadata.fileName,
        title: context.metadata.exportTitle ?? context.metadata.fileName ?? 'Export',
        timestamp,
    };
}

function csvSafe(value: AppExportValue): string {
    if (value === null || value === undefined) {
        return '';
    }

    let text = value instanceof Date ? value.toISOString() : String(value);

    if (typeof value === 'string' && /^[\s\u0000-\u001f]*[=+\-@]/.test(text)) {
        text = `'${text}`;
    }

    return `"${text.replace(/"/g, '""')}"`;
}

export function serializeAppExportCsv(projection: AppExportProjection): string {
    const rows: AppExportValue[][] = [];

    for (const metadata of projection.metadataRows) rows.push([metadata]);

    if (projection.metadataRows.length) {
        rows.push([]);
    }
    rows.push(...projection.headerRows.map((row) => [...row]));

    for (const row of projection.rows) rows.push([...row.values]);

    return rows.map((row) => row.map(csvSafe).join(',')).join('\r\n');
}

function excelValue(value: AppExportValue): string | number | boolean | Date | null {
    if (value === undefined || value === null) {
        return null;
    }

    return value;
}

export function normalizeAppExportBuffer(value: unknown): ArrayBuffer {
    if (value instanceof ArrayBuffer) {
        return value.slice(0);
    }

    if (ArrayBuffer.isView(value)) {
        const source = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
        const copy = new Uint8Array(value.byteLength);

        copy.set(source);

        return copy.buffer;
    }
    throw new TypeError('ExcelJS returned an unsupported workbook buffer.');
}

/** Build an XLSX file lazily so loading table code does not load ExcelJS. */
export async function createAppExportWorkbook(projection: AppExportProjection): Promise<ArrayBuffer> {
    const ExcelJSModule = await import('exceljs');
    const workbook = new ExcelJSModule.Workbook();
    const worksheet = workbook.addWorksheet('Export');
    const width = Math.max(1, projection.columns.length);

    for (const metadata of projection.metadataRows) {
        const row = worksheet.addRow([metadata]);

        if (width > 1) {
            worksheet.mergeCells(row.number, 1, row.number, width);
        }
    }

    if (projection.metadataRows.length) {
        worksheet.addRow([]);
    }

    const headerStart = worksheet.rowCount + 1;

    projection.headerRows.forEach((values) => {
        const row = worksheet.addRow(values);

        row.font = {
            bold: true,
        };
        row.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true,
        };
    });

    for (const merge of projection.headerMerges)
        worksheet.mergeCells(
            headerStart + merge.rowIndex,
            merge.startColumn + 1,
            headerStart + merge.rowIndex,
            merge.endColumn,
        );
    worksheet.columns = projection.columns.map((column) => ({
        width: 20,
        style:
            column.scalarType === 'date'
                ? {
                      numFmt: 'dd/mm/yyyy',
                  }
                : column.scalarType === 'dateTime'
                  ? {
                        numFmt: 'dd/mm/yyyy hh:mm',
                    }
                  : column.scalarType === 'number'
                    ? {
                          numFmt: '#,##0.##;[Red]-#,##0.##',
                      }
                    : undefined,
    }));

    for (const row of projection.rows) {
        const excelRow = worksheet.addRow(row.values.map(excelValue));

        if (row.kind === 'group' || row.kind === 'grandTotal') {
            excelRow.font = {
                bold: true,
            };
        }
    }

    const buffer = await workbook.xlsx.writeBuffer();

    return normalizeAppExportBuffer(buffer);
}

function escapeHtml(value: AppExportValue): string {
    const text =
        value === null || value === undefined ? '' : value instanceof Date ? value.toISOString() : String(value);

    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function renderHtmlRow(
    values: readonly AppExportValue[],
    rowKind?: AppProjectedRow['kind'],
    displayValues: readonly string[] = values.map((value) =>
        value === null || value === undefined ? '' : String(value),
    ),
): string {
    const tag = rowKind === 'group' || rowKind === 'grandTotal' ? 'th' : 'td';

    return `<tr class="${rowKind ?? 'header'}">${displayValues.map((value) => `<${tag}>${escapeHtml(value)}</${tag}>`).join('')}</tr>`;
}

function renderHtmlHeaderRow(projection: AppExportProjection, rowIndex: number): string {
    const merges = projection.headerMerges.filter((merge) => merge.rowIndex === rowIndex);
    const cells: string[] = [];

    for (let columnIndex = 0; columnIndex < projection.columns.length; ) {
        const merge = merges.find((candidate) => candidate.startColumn === columnIndex);
        const span = merge ? merge.endColumn - merge.startColumn : 1;
        const label = projection.headerRows[rowIndex][columnIndex] ?? '';

        cells.push(`<th${span > 1 ? ` colspan="${span}"` : ''}>${escapeHtml(label)}</th>`);
        columnIndex += span;
    }

    return `<tr class="header">${cells.join('')}</tr>`;
}

/** Render a complete standalone print document from projected data, including every filtered leaf. */
export function createAppExportPrintHtml(projection: AppExportProjection): string {
    const title = projection.title;
    const metadata = projection.timestamp ? `<p class="timestamp">${escapeHtml(projection.timestamp)}</p>` : '';
    const header = projection.headerRows.map((_row, index) => renderHtmlHeaderRow(projection, index)).join('');
    const body = projection.rows.map((row) => renderHtmlRow(row.values, row.kind, row.displayValues)).join('');

    return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'"><title>${escapeHtml(title)}</title><style>@page{size:landscape;margin:12mm}body{font:12px Arial,sans-serif;color:#111}h1{font-size:18px;margin:0 0 4px}.timestamp{margin:0 0 12px;color:#555}table{border-collapse:collapse;width:100%;table-layout:auto}th,td{border:1px solid #999;padding:4px 6px;text-align:left;vertical-align:top;overflow-wrap:anywhere}thead tr,tbody tr.group,tbody tr.grandTotal{background:#eee}tbody tr.group th:first-child{white-space:pre-wrap}tbody tr.group th,tbody tr.grandTotal th{font-weight:bold}@media print{thead{display:table-header-group}tr{break-inside:avoid}}</style></head><body><h1>${escapeHtml(title)}</h1>${metadata}<table><thead>${header}</thead><tbody>${body}</tbody></table></body></html>`;
}

/** Open and print a standalone full-data document; call directly from a user gesture. */
export function printAppExport(
    projection: AppExportProjection,
    openWindow: typeof window.open = window.open.bind(window),
): boolean {
    const printWindow = openWindow('', '_blank');

    if (!printWindow) {
        return false;
    }
    printWindow.document.open();
    printWindow.document.write(createAppExportPrintHtml(projection));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();

    return true;
}
